import {
  Box,
  HeadingText,
  StackLayout,
  useMediaQuery,
  queryHelpers,
  UtilityText,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import { FormattedMessage, useIntl } from '@leagueplatform/locales';
import { useDocumentTitle } from '@leagueplatform/web-common';
import {
  LoadingIndicator,
  PageHeaderBackground,
} from '@leagueplatform/web-common-components';
import { renderPersonalInfoLink } from 'common/links';
import { ConnectionErrorPage } from 'pages/errors/connection-error/connection-error.page';
import { usePersonalInfo } from './use-personal-info.hook';
import { MaskedValue } from './masked-value.component';

export const PersonalInfoPage = () => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));
  useDocumentTitle(formatMessage({ id: 'STR_PERSONAL_INFO' }));

  const { fields, isLoading, isError, refetch } = usePersonalInfo();

  if (isLoading) return <LoadingIndicator />;
  if (isError) return <ConnectionErrorPage retry={refetch} />;

  return (
    <>
      {/* Header height is controlled globally via .cc-page-header-background in index.css */}
      <PageHeaderBackground
        alignItems="center"
        width="100%"
        maxWidth="950px"
        marginX="auto"
      >
        <HeadingText
          level="1"
          size={{ '@initial': 'xxl', '@mobile': 'xl' }}
          css={{ paddingInlineStart: isMobile ? '$oneAndHalf' : '$none' }}
        >
          {formatMessage({ id: 'STR_PERSONAL_INFO' })}
        </HeadingText>
      </PageHeaderBackground>

      <Box
        css={{
          padding: isMobile ? '$two $oneAndHalf' : '$two $none',
          maxWidth: '950px',
          marginX: 'auto',
          gap: '$twoAndHalf',
        }}
      >
        <ParagraphText css={{ marginBottom: '$twoAndHalf' }}>
          <FormattedMessage
            id="STR_ACCOUNT_SETUP_MYCHART_INFO"
            values={{ myChartLink: renderPersonalInfoLink('$subtitleOne') }}
          />
        </ParagraphText>

        <StackLayout
          orientation="vertical"
          css={{ gap: '$oneAndHalf', maxWidth: '604px' }}
        >
          <Box
            css={{
              boxShadow: '$card',
              border: '1px solid $onSurfaceBorderSubdued',
              padding: '$oneAndHalf',
              width: '100%',
            }}
          >
            <HeadingText level="2" size="lg" css={{ marginBottom: '$one' }}>
              {formatMessage({ id: 'STR_PERSONAL_INFO' })}
            </HeadingText>

            {fields.map(({ labelId, value, forceLtr, sensitive }, index) => (
              <Box
                key={labelId}
                css={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: isMobile ? 'flex-start' : 'space-between',
                  alignItems: 'flex-start',
                  gap: isMobile && !sensitive ? '$threeQuarters' : '$none',
                  padding: isMobile ? '$threeQuarters $none' : '$half $none',
                  ...(index < fields.length - 1
                    ? { borderBottom: '1px solid $onSurfaceBorderSubdued' }
                    : {}),
                }}
              >
                <UtilityText
                  as="h3"
                  css={{
                    typography: '$label',
                    paddingTop: isMobile ? '$none' : '6px',
                    paddingBottom: isMobile ? '$none' : '6px',
                  }}
                >
                  {formatMessage({ id: labelId })}
                </UtilityText>
                {sensitive ? (
                  <MaskedValue
                    value={value as string}
                    dir={forceLtr ? 'ltr' : undefined}
                    css={{
                      textAlign: isMobile ? 'start' : 'end',
                      typography: '$subtitleTwo',
                      paddingTop: isMobile ? '$none' : '6px',
                      paddingBottom: isMobile ? '$none' : '6px',
                      fontWeight: 'bold',
                    }}
                  />
                ) : (
                  <UtilityText
                    dir={forceLtr ? 'ltr' : undefined}
                    css={{
                      textAlign: isMobile ? 'start' : 'end',
                      typography: '$subtitleTwo',
                      paddingTop: isMobile ? '$none' : '6px',
                      paddingBottom: isMobile ? '$none' : '6px',
                      fontWeight: 'bold',
                    }}
                  >
                    {value}
                  </UtilityText>
                )}
              </Box>
            ))}
          </Box>
        </StackLayout>
      </Box>
    </>
  );
};
